import styled from "styled-components";

import type { ChatSummary } from "../api/types";
import { moodColor, moodLabel } from "../lib/format";
import { theme } from "../theme";
import { Meter } from "./Meter";
import { Card } from "./primitives";

// Same animated mascot asset used on Home / the design's chat avatars.
const MASCOT = "https://racccoin.org/raccmascot_anim.webp";

// Joined rows are a full-card tap target (open that chat); discover rows are a
// plain card whose only action is the "join" button.
const OpenTap = styled.button`
  appearance: none;
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:active {
    transform: scale(0.995);
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.color.border};
  background: radial-gradient(
    120% 120% at 50% 0%,
    ${({ theme }) => theme.color.accentSoft},
    ${({ theme }) => theme.color.surfaceRaised}
  );

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`;

const Meta = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: ${({ theme }) => theme.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Dot = styled.span<{ $color: string }>`
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 6px ${({ $color }) => $color};
`;

const MoodText = styled.span<{ $color: string }>`
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ $color }) => $color};
`;

const Sub = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 2px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textFaint};
  overflow: hidden;
  white-space: nowrap;
`;

const Chevron = styled.span`
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 18px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const JoinBtn = styled.button`
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.35);
  color: ${({ theme }) => theme.color.accent};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const Meters = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.color.border};

  & > * {
    flex: 1;
  }
`;

const Rank = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const RankValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.accent};
`;

export function ChatRow({
  chat,
  onOpen,
  onJoin,
}: {
  chat: ChatSummary;
  onOpen: (chatId: number) => void;
  onJoin: (chat: ChatSummary) => void;
}) {
  const { pet } = chat;
  const handle = chat.username ? `@${chat.username}` : "private";
  const dot = pet ? moodColor(pet.mood, pet.dead) : theme.color.asleep;

  const content = (
    <Card>
      <Top>
        <Avatar>
          <img src={MASCOT} alt="" />
        </Avatar>
        <Meta>
          <NameRow>
            <Title>{chat.title}</Title>
            {pet && <Dot $color={dot} />}
            {pet && <MoodText $color={dot}>{pet.dead ? "dead" : moodLabel(pet.mood)}</MoodText>}
          </NameRow>
          <Sub>
            <span>{handle}</span>
            <span>· {chat.memberCount.toLocaleString()} members</span>
            {pet && <span>· day {pet.ageDays}</span>}
            {pet && <span>· lv {pet.level}</span>}
          </Sub>
        </Meta>
        {chat.joined ? (
          <Chevron aria-hidden>›</Chevron>
        ) : (
          <JoinBtn type="button" onClick={() => onJoin(chat)}>
            join
          </JoinBtn>
        )}
      </Top>

      {pet && (
        <Meters>
          <Meter label="sat" value={pet.satiety} color={theme.color.satiety} icon="◍" />
          <Meter label="hyg" value={pet.hygiene} color={theme.color.hygiene} icon="◇" />
          <Meter label="happy" value={pet.happiness} color={theme.color.happiness} icon="✦" />
        </Meters>
      )}

      {chat.joined && chat.viewerRank !== null && (
        <Rank>
          your rank here <RankValue>#{chat.viewerRank}</RankValue>
        </Rank>
      )}
    </Card>
  );

  if (chat.joined) {
    return <OpenTap onClick={() => onOpen(chat.chatId)}>{content}</OpenTap>;
  }
  return content;
}
